import _ from "lodash";
import Loading from "common/ui/Loading";
import useUniqueId from "common/ui/useUniqueId";
import React, { useState } from "react";
import { Form } from "react-bootstrap";
import NotificationPreference from "./notificationPreference";
import notificationService from "./notificationService";
import "./NotificationSettings.scss";
import useAjaxCall from "common/ui/useAjaxCall";
import { logHttpError, withCancellationToken } from "common/http/httpService";

enum OfferContractSource {
    Mine = 1,
    All
}

type OfferContractRule = {
    isEmailEnabled: boolean;
    isSmsEnabled: boolean;
    offerSource: OfferContractSource;
};

enum OfferCreatedSource {
    MyOffers = 0,
    MyCustomers,
    AllCustomers
}

type OfferCreatedRule = {
    isEmailEnabled: boolean;
    isSmsEnabled: boolean;
    offerSource: OfferCreatedSource;
};

type NotificationEnabledRule = {
    isEnabled: boolean;
};

type PreferenceProps<TRule> = {
    myOfferSourceLabel?: string;
    allOfferSourceLabel?: string;
    rule: TRule;
    onRuleChange(rule: TRule): void;
    children: any;
    ignorePreferenceSource?: boolean;
};

const OfferContractPreference: React.FC<PreferenceProps<OfferContractRule>> = (props) => {
    const id = useUniqueId();

    return <div className="preference">
        <div className="row justify-content-between header-row">
            <div className="col-5 rule-name">{props.children}</div>
            <div className="col text-right">
                <Form.Check type="switch" label="Email Me"
                    id={`email${id}`}
                    inline
                    checked={props.rule.isEmailEnabled}
                    onChange={(ev: React.ChangeEvent<HTMLInputElement>) => props.onRuleChange({ ...props.rule, isEmailEnabled: ev.target.checked })} />
            </div>
            <div className="col text-right">
                <Form.Check type="switch" label="Text Me"
                    id={`sms${id}`}
                    inline
                    checked={props.rule.isSmsEnabled}
                    onChange={(ev: React.ChangeEvent<HTMLInputElement>) => props.onRuleChange({ ...props.rule, isSmsEnabled: ev.target.checked })} />
            </div>
        </div>
        {!props.ignorePreferenceSource && <div className="row">
            <div className="col">
                <Form.Check inline type="radio" label={props.myOfferSourceLabel ?? "My Offers"}
                    checked={props.rule.offerSource === OfferContractSource.Mine}
                    disabled={!props.rule.isEmailEnabled && !props.rule.isSmsEnabled}
                    onChange={() => props.onRuleChange({ ...props.rule, offerSource: OfferContractSource.Mine })} />
                <Form.Check inline type="radio" label={props.allOfferSourceLabel ?? "All Offers"}
                    checked={props.rule.offerSource === OfferContractSource.All}
                    disabled={!props.rule.isEmailEnabled && !props.rule.isSmsEnabled}
                    onChange={() => props.onRuleChange({ ...props.rule, offerSource: OfferContractSource.All })} />
            </div>
        </div>}
    </div>
}

const OfferCreatedPreference: React.FC<PreferenceProps<OfferCreatedRule>> = (props) => {
    const id = useUniqueId();

    return <div className="preference">
        <div className="row justify-content-between header-row">
            <div className="col-5 rule-name">{props.children}</div>
            <div className="col text-right">
                <Form.Check type="switch" label="Email Me"
                    id={`email${id}`}
                    inline
                    checked={props.rule.isEmailEnabled}
                    onChange={(ev: React.ChangeEvent<HTMLInputElement>) => props.onRuleChange({ ...props.rule, isEmailEnabled: ev.target.checked })} />
            </div>
            <div className="col text-right">
                <Form.Check type="switch" label="Text Me"
                    id={`sms${id}`}
                    inline
                    checked={props.rule.isSmsEnabled}
                    onChange={(ev: React.ChangeEvent<HTMLInputElement>) => props.onRuleChange({ ...props.rule, isSmsEnabled: ev.target.checked })} />
            </div>
        </div>
        {!props.ignorePreferenceSource && <div className="row">
            <div className="col">
                <Form.Check inline type="radio" label="My Offers"
                    checked={props.rule.offerSource === OfferCreatedSource.MyOffers}
                    disabled={!props.rule.isEmailEnabled && !props.rule.isSmsEnabled}
                    onChange={() => props.onRuleChange({ ...props.rule, offerSource: OfferCreatedSource.MyOffers })} />
                <Form.Check inline type="radio" label="My Customers"
                    checked={props.rule.offerSource === OfferCreatedSource.MyCustomers}
                    disabled={!props.rule.isEmailEnabled && !props.rule.isSmsEnabled}
                    onChange={() => props.onRuleChange({ ...props.rule, offerSource: OfferCreatedSource.MyCustomers })} />
                <Form.Check inline type="radio" label="All Customers"
                    checked={props.rule.offerSource === OfferCreatedSource.AllCustomers}
                    disabled={!props.rule.isEmailEnabled && !props.rule.isSmsEnabled}
                    onChange={() => props.onRuleChange({ ...props.rule, offerSource: OfferCreatedSource.AllCustomers })} />
            </div>
        </div>}
    </div>
}

const NotificationEnabledPreference: React.FC<PreferenceProps<NotificationEnabledRule>> = (props) => {
    const id = useUniqueId();

    return <div className="preference">
        <div className="row justify-content-between header-row">
            <div className="col-5 rule-name">{props.children}</div>
            <div className="col text-right">
                <Form.Check type="switch" label="Enabled"
                    id={`enabled${id}`}
                    inline
                    checked={props.rule.isEnabled}
                    onChange={(ev: React.ChangeEvent<HTMLInputElement>) => props.onRuleChange({ ...props.rule, isEnabled: ev.target.checked })} />
            </div>
        </div>
    </div>
}

const Preference: React.FC<{ pref: NotificationPreference, onRuleChange(rule: any): void }> = (props) => {
    switch (props.pref.notificationType) {
        case "OfferCreated":
            return <OfferCreatedPreference myOfferSourceLabel="My Customers" allOfferSourceLabel="All Customers"
                rule={props.pref.rule} onRuleChange={props.onRuleChange}>Trader Created Offer</OfferCreatedPreference>
        case "OfferCreatedByCustomer":
            return <OfferContractPreference myOfferSourceLabel="My Customers" allOfferSourceLabel="All Customers"
                rule={props.pref.rule} onRuleChange={props.onRuleChange}>Customer Created Offer</OfferContractPreference>
        case "OfferUpdated":
            return <OfferContractPreference rule={props.pref.rule} onRuleChange={props.onRuleChange}>Offer Updated</OfferContractPreference>;
        case "OfferFilled":
            return <OfferContractPreference rule={props.pref.rule} onRuleChange={props.onRuleChange}>Offer Fills</OfferContractPreference>
        case "OfferExpiring":
            return <OfferContractPreference rule={props.pref.rule} onRuleChange={props.onRuleChange}>Offer Expiring</OfferContractPreference>
        case "ContractExpiring":
            return <OfferContractPreference rule={props.pref.rule} onRuleChange={props.onRuleChange} ignorePreferenceSource>Contract Expiring</OfferContractPreference>
        case "OfferExpired":
            return <OfferContractPreference rule={props.pref.rule} onRuleChange={props.onRuleChange}>Offer Expires</OfferContractPreference>
        case "OrderError":
            return <OfferContractPreference rule={props.pref.rule} onRuleChange={props.onRuleChange}>Order Errors</OfferContractPreference>
        case "PositionOrderFilled":
            return <OfferContractPreference rule={props.pref.rule} onRuleChange={props.onRuleChange} ignorePreferenceSource>Position Fills</OfferContractPreference>
        case "OfferPending":
            return <OfferContractPreference rule={props.pref.rule} onRuleChange={props.onRuleChange} ignorePreferenceSource>Pending Offers</OfferContractPreference>
        case "BidChanged":
            return <OfferContractPreference rule={props.pref.rule} onRuleChange={props.onRuleChange} ignorePreferenceSource>Bids Updated</OfferContractPreference>
        case "ContractBasisPushed":
            return <NotificationEnabledPreference rule={props.pref.rule} onRuleChange={props.onRuleChange}>Contract Basis Pushed</NotificationEnabledPreference>
        default:
            return <></>
    }
}

const NotificationSettings: React.FC = () => {
    const [preferences, setPreferences] = useState<NotificationPreference[]>(null);

    useAjaxCall(cxt => {
        try {
            withCancellationToken(notificationService, cxt).listPreferences()
                .then(prefs => setPreferences(_.sortBy(prefs, m => m.displayOrder)));
        } catch (error) {
            logHttpError("Unable to list preferences", error);
        }
    }, []);

    function onRuleChange(preference: NotificationPreference): void {
        setPreferences(preferences.map(m => m.notificationType === preference.notificationType
            ? preference
            : m));
        notificationService.updatePreference(preference);
    }

    const isLoaded = preferences != null;

    return <div className="notification-settings">
        {!isLoaded && <Loading />}
        {isLoaded && preferences.map(m => <Preference key={m.notificationType} pref={m} onRuleChange={(rule) => onRuleChange({ ...m, rule: rule })} />)}
    </div>
}


export default NotificationSettings;